import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from '../../components/DirectionalIcon';
import { colors, typography } from '../../theme';
import { designTokens } from '../../theme/design-system';
import { FlowSessionItem } from '../../services/api';
import { useThemedStyles } from '../../hooks/useThemedStyles';

const CHANNEL_ICON: Record<string, string> = {
  whatsapp: 'logo-whatsapp',
  facebook_messenger: 'logo-facebook',
  x_twitter: 'logo-twitter',
};

function timeAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

export interface FlowSessionsCardProps {
  sessions: FlowSessionItem[];
  total: number;
  onViewAll?: () => void;
}

/** Ongoing/pending flows — contacts currently paused mid-flow awaiting their
 * next message (GET /api/omni-channel/flow-sessions, Session 3). */
export const FlowSessionsCard: React.FC<FlowSessionsCardProps> = ({ sessions, total, onViewAll }) => {
  const styles = useThemedStyles(getStyles);
  return (
    <View style={styles.card}>
      <View style={styles.header}>
        <Text style={styles.title}>Ongoing flows</Text>
        {total > 0 && <Text style={styles.count}>{total}</Text>}
      </View>
      {sessions.length === 0 ? (
        <View style={styles.empty}>
          <Ionicons name="git-branch-outline" size={22} color={colors.text.secondary as string} />
          <Text style={styles.emptyText}>No contacts are mid-flow right now</Text>
        </View>
      ) : (
        <View style={styles.list}>
          {sessions.map((s) => (
            <View key={s.id} style={styles.row}>
              <Ionicons name={(CHANNEL_ICON[s.channel] ?? 'chatbubble-outline') as any} size={16} color={colors.brand.primary as string} />
              <View style={styles.rowContent}>
                <Text style={styles.flowName} numberOfLines={1}>{s.flowName}</Text>
                <Text style={styles.meta} numberOfLines={1}>Contact {s.contactId.slice(-6)} · {timeAgo(s.lastAdvancedAt)}</Text>
              </View>
            </View>
          ))}
        </View>
      )}

      {onViewAll && sessions.length > 0 && (
        <TouchableOpacity style={styles.viewAll} onPress={onViewAll} activeOpacity={0.7}>
          <Text style={styles.viewAllText}>View all</Text>
          <DirectionalIcon name="chevron-forward" size={14} color={colors.brand.primary as string} />
        </TouchableOpacity>
      )}
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: designTokens.spacing.md,
    ...designTokens.shadows.sm,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: designTokens.spacing.sm,
  },
  title: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.primary,
  },
  count: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.bold,
    color: colors.brand.primary,
    backgroundColor: colors.primaryTransparent,
    paddingHorizontal: designTokens.spacing.xs,
    paddingVertical: 2,
    borderRadius: designTokens.borderRadius.full,
    overflow: 'hidden',
  },
  list: {
    gap: designTokens.spacing.sm,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: designTokens.spacing.sm,
  },
  rowContent: {
    flex: 1,
  },
  flowName: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.primary,
  },
  meta: {
    fontSize: typography.fontSize.xs,
    color: colors.text.secondary,
  },
  empty: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: designTokens.spacing.xs,
    paddingVertical: designTokens.spacing.lg,
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    color: colors.text.secondary,
    textAlign: 'center',
  },
  viewAll: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    marginTop: designTokens.spacing.sm,
    paddingTop: designTokens.spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  viewAllText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.brand.primary,
  },
});
